import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { demoApplications, statusConfig, getNextSteps, applicationTimeline } from '@/mocks/radioApplication';
import type { RadioApplication, ApplicationStatus } from '@/mocks/radioApplication';
import Logo from '@/components/feature/Logo';

const STATUS_ORDER: ApplicationStatus[] = ['pending', 'approved', 'rejected', 'needs_changes', 'suspended'];

function getTimelinePosition(status: ApplicationStatus): number {
  const map: Record<ApplicationStatus, number> = {
    pending: 0,
    approved: 3,
    rejected: 4,
    needs_changes: 2,
    suspended: 4,
  };
  return map[status];
}

function getConnectingStates(status: ApplicationStatus): ApplicationStatus[] {
  switch (status) {
    case 'pending':
      return ['pending'];
    case 'approved':
      return ['pending', 'approved'];
    case 'rejected':
      return ['pending', 'rejected'];
    case 'needs_changes':
      return ['pending', 'needs_changes'];
    case 'suspended':
      return ['pending', 'approved', 'suspended'];
    default:
      return [];
  }
}

export default function ApplicationStatusPage() {
  const navigate = useNavigate();
  const [activeState, setActiveState] = useState<ApplicationStatus>('pending');

  const app: RadioApplication = demoApplications.find((a) => a.status === activeState) || demoApplications[0];
  const cfg = statusConfig[app.status];
  const isFree = app.planId === 'free';
  const isPaid = app.planId === 'pro' || app.planId === 'premium';
  const nextSteps = getNextSteps(app.status, app.planId);
  const publicComments = app.teamComments.filter((c) => !c.isInternal);
  const internalComments = app.teamComments.filter((c) => c.isInternal);
  const connectedStates = getConnectingStates(app.status);
  const timelinePosition = getTimelinePosition(app.status);

  return (
    <div className="min-h-screen bg-background-100/70 flex flex-col">
      {/* Top bar */}
      <header className="w-full bg-background-50 border-b border-background-200 sticky top-0 z-30">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 flex items-center justify-between h-14 md:h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-foreground-500 hover:text-foreground-800 cursor-pointer transition-colors"
          >
            <Logo size="sm" />
            <span className="text-sm font-bold text-foreground-800 hidden sm:inline">RadioFrecuenciaWeb</span>
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-dashboard-line mr-1"></i>
              Panel
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* State demo switcher */}
        <div className="mb-8">
          <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">
            Demo — Selecciona un estado para previsualizar
          </p>
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 flex-wrap">
            {STATUS_ORDER.map((st) => {
              const sc = statusConfig[st];
              const isActive = activeState === st;
              return (
                <button
                  key={st}
                  onClick={() => setActiveState(st)}
                  className={`whitespace-nowrap px-3.5 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all border ${
                    isActive
                      ? `${sc.bgClass} ${sc.textClass} ${sc.borderClass}`
                      : 'bg-background-50 text-foreground-500 border-background-200 hover:border-background-300'
                  }`}
                >
                  <i className={`${sc.icon} mr-1.5`}></i>
                  {sc.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Status Banner */}
        <div className={`rounded-2xl ${cfg.bgClass} border ${cfg.borderClass} p-6 md:p-8 mb-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center shrink-0 ${
              app.status === 'approved'
                ? 'bg-green-200'
                : app.status === 'rejected' || app.status === 'suspended'
                ? 'bg-red-200'
                : app.status === 'needs_changes'
                ? 'bg-orange-200'
                : 'bg-amber-200'
            }`}>
              <i className={`${cfg.icon} text-2xl ${cfg.textClass}`}></i>
            </div>
            <div className="flex-1">
              <h1 className="text-lg md:text-xl font-bold text-foreground-950 mb-1">
                {app.radioName}
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${cfg.bgClass} ${cfg.textClass} border ${cfg.borderClass}`}>
                  <i className={`${cfg.icon} text-xs`}></i>
                  {cfg.label}
                </span>
                <span className="text-sm font-semibold text-foreground-700">{app.frequency}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  app.planId === 'premium'
                    ? 'bg-accent-100 text-accent-700'
                    : app.planId === 'pro'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  Plan {app.planName}
                </span>
              </div>
              <p className="text-sm text-foreground-500 mt-2">{cfg.description}</p>
            </div>
          </div>
        </div>

        {/* Plan context box */}
        <div className={`rounded-xl border p-4 mb-6 ${
          isFree
            ? 'bg-amber-50/70 border-amber-200'
            : 'bg-green-50/70 border-green-200'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
              isFree ? 'bg-amber-200' : 'bg-green-200'
            }`}>
              <i className={`text-sm ${isFree ? 'ri-time-line text-amber-700' : 'ri-flashlight-line text-green-700'}`}></i>
            </div>
            <div>
              <p className="text-sm font-bold text-foreground-800 mb-1">
                {isFree
                  ? 'Plan Free — Requiere revisión manual'
                  : `Plan ${app.planName} — Activación inmediata con revisión posterior`}
              </p>
              <p className="text-xs text-foreground-500 leading-relaxed">
                {isFree
                  ? 'Las radios en plan Free pasan por un proceso de revisión para asegurar la calidad del dial digital. El equipo verifica que el stream esté activo, que los datos de contacto sean reales y que el contenido sea original. Este proceso toma entre 24 y 48 horas hábiles.'
                  : 'Las radios en planes pagos se activan al instante. Podés empezar a transmitir ya mismo. El equipo hace una revisión de calidad posterior para asegurarse de que todo funcione correctamente y sugerir mejoras.'}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: details + timeline */}
          <div className="lg:col-span-2 space-y-6">
            {/* Application details card */}
            <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
              <div className="px-5 md:px-6 py-4 border-b border-background-100">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-foreground-400"></i>
                  Datos de la solicitud
                </h3>
              </div>
              <div className="p-5 md:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">Nombre de la radio</p>
                    <p className="text-sm font-semibold text-foreground-900">{app.radioName}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">Frecuencia solicitada</p>
                    <p className="text-sm font-bold text-foreground-900">{app.frequency}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">Plan</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                      app.planId === 'premium'
                        ? 'bg-accent-100 text-accent-700'
                        : app.planId === 'pro'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {app.planName}
                    </span>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">Categoría</p>
                    <p className="text-sm text-foreground-700">{app.category}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">País / Ciudad</p>
                    <p className="text-sm text-foreground-700">{app.country}{app.city ? `, ${app.city}` : ''}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">Stream URL</p>
                    <p className="text-sm text-foreground-600 font-mono truncate">{app.streamUrl}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">Fecha de solicitud</p>
                    <p className="text-sm text-foreground-700">{app.submittedAt}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">Última actualización</p>
                    <p className="text-sm text-foreground-700">{app.updatedAt}</p>
                  </div>
                </div>
                {app.slogan && (
                  <div className="mt-4 pt-4 border-t border-background-100">
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">Eslogan</p>
                    <p className="text-sm text-foreground-600 italic">&quot;{app.slogan}&quot;</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rejection reason */}
            {app.status === 'rejected' && app.rejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5 md:p-6">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-red-200 flex items-center justify-center shrink-0">
                    <i className="ri-close-circle-line text-red-700 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-red-800 mb-1">Motivo del rechazo</h4>
                    <p className="text-sm text-red-700 leading-relaxed">{app.rejectionReason}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Suspension reason */}
            {app.status === 'suspended' && app.suspensionReason && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5 md:p-6">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-red-200 flex items-center justify-center shrink-0">
                    <i className="ri-forbid-line text-red-700 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-red-800 mb-1">Motivo de la suspensión</h4>
                    <p className="text-sm text-red-700 leading-relaxed">{app.suspensionReason}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Changes requested */}
            {app.status === 'needs_changes' && app.changesRequested && (
              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 md:p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-orange-200 flex items-center justify-center shrink-0">
                    <i className="ri-edit-line text-orange-700 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-orange-800 mb-1">Cambios solicitados</h4>
                    <p className="text-xs text-orange-600">El equipo te pide las siguientes modificaciones:</p>
                  </div>
                </div>
                <ul className="space-y-2.5 ml-12">
                  {app.changesRequested.map((change, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-orange-200 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[10px] font-bold text-orange-700">{i + 1}</span>
                      </div>
                      <span className="text-sm text-foreground-700">{change}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Team comments */}
            {(publicComments.length > 0 || internalComments.length > 0) && (
              <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
                <div className="px-5 md:px-6 py-4 border-b border-background-100">
                  <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                    <i className="ri-chat-3-line text-foreground-400"></i>
                    Comentarios del equipo
                  </h3>
                </div>
                <div className="p-5 md:p-6 space-y-4">
                  {publicComments.map((comment) => (
                    <div key={comment.id} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-[10px] font-bold text-primary-600">
                          {comment.author.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-bold text-foreground-800">{comment.author}</span>
                          <span className="text-[10px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-full">{comment.role}</span>
                          <span className="text-[10px] text-foreground-400">{comment.date}</span>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">{comment.text}</p>
                      </div>
                    </div>
                  ))}

                  {internalComments.length > 0 && (
                    <>
                      <div className="flex items-center gap-3 pt-2">
                        <div className="flex-1 h-px bg-background-200"></div>
                        <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider whitespace-nowrap">
                          Notas internas del equipo
                        </span>
                        <div className="flex-1 h-px bg-background-200"></div>
                      </div>
                      {internalComments.map((comment) => (
                        <div key={comment.id} className="flex gap-3 opacity-70">
                          <div className="w-8 h-8 rounded-full bg-foreground-200 flex items-center justify-center shrink-0 mt-0.5">
                            <i className="ri-lock-line text-foreground-500 text-xs"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-bold text-foreground-600">{comment.author}</span>
                              <span className="text-[10px] text-foreground-400 bg-foreground-100 px-1.5 py-0.5 rounded-full">{comment.role}</span>
                              <span className="text-[10px] text-foreground-400">{comment.date}</span>
                            </div>
                            <p className="text-sm text-foreground-500 leading-relaxed">{comment.text}</p>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right column: timeline + next steps */}
          <div className="space-y-6">
            {/* Timeline */}
            <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-git-branch-line text-foreground-400"></i>
                  Progreso
                </h3>
              </div>
              <div className="p-5">
                <div className="space-y-0">
                  {applicationTimeline.map((step, idx) => {
                    const isConnected = connectedStates.includes(step.status);
                    const isCurrent = step.status === app.status;
                    const isPast = isConnected && !isCurrent;

                    return (
                      <div key={step.status} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all ${
                            isCurrent
                              ? `${cfg.bgClass} ${cfg.textClass} ring-2 ${cfg.borderClass} scale-110`
                              : isPast
                              ? 'bg-green-100 text-green-600'
                              : 'bg-background-200 text-foreground-400'
                          }`}>
                            {isPast ? (
                              <i className="ri-check-line text-xs font-bold"></i>
                            ) : (
                              <i className={`${step.status === 'rejected' || step.status === 'suspended' ? 'ri-close-circle-line' : 'ri-record-circle-line'} text-xs`}></i>
                            )}
                          </div>
                          {idx < applicationTimeline.length - 1 && (
                            <div className={`w-0.5 h-8 ${isPast ? 'bg-green-200' : 'bg-background-200'}`}></div>
                          )}
                        </div>
                        <div className="pb-6">
                          <p className={`text-sm font-semibold leading-none mt-2 mb-1 ${
                            isCurrent ? 'text-foreground-950' : isPast ? 'text-foreground-600' : 'text-foreground-400'
                          }`}>
                            {step.label}
                          </p>
                          {isCurrent && (
                            <p className="text-[11px] text-foreground-500">Actual</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Next steps */}
            <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-100">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-footprint-line text-foreground-400"></i>
                  Próximos pasos
                </h3>
              </div>
              <div className="p-5 space-y-3">
                {nextSteps.map((step, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                      idx === 0 ? `${cfg.bgClass} ${cfg.textClass}` : 'bg-background-100 text-foreground-500'
                    }`}>
                      <i className={`${step.icon} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-foreground-800 mb-0.5">{step.title}</p>
                      <p className="text-[11px] text-foreground-500 leading-relaxed">{step.description}</p>
                      {step.action && (
                        <button
                          type="button"
                          onClick={() => {
                            if (step.action === 'Ir al panel') navigate('/admin/dashboard');
                            else if (step.action === 'Volver a registrar') navigate('/register-radio');
                          }}
                          className="mt-2 px-3 py-1.5 text-[10px] font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                        >
                          {step.action}
                          <i className="ri-arrow-right-line ml-1"></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reviewer info */}
            {app.reviewerName && (
              <div className="bg-background-50 rounded-2xl border border-background-200 p-5">
                <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">
                  Revisor asignado
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-bold text-primary-600">
                      {app.reviewerName.split(' ').map((n) => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{app.reviewerName}</p>
                    <p className="text-[11px] text-foreground-400">Equipo de revisión</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer strip */}
      <footer className="w-full bg-background-50 border-t border-background-200 py-4 mt-auto">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-[11px] text-foreground-400">
            &copy; {new Date().getFullYear()} RadioFrecuenciaWeb
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="text-[11px] text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors">Centro de ayuda</a>
            <a href="#" className="text-[11px] text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors">Términos</a>
            <a href="#" className="text-[11px] text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors">Contactar soporte</a>
          </div>
        </div>
      </footer>
    </div>
  );
}